import React, { useState } from "react";
import { Box, CircularProgress, IconButton, useTheme } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import CustomTypography from "../form/CustomTypography";

interface PercentageStatCardProps {
  value: number | null | undefined;
  title: string;
  isSecure?: boolean;
}

const PercentageStatCard: React.FC<PercentageStatCardProps> = ({
  value,
  title,
  isSecure = false,
}) => {
  const theme = useTheme();
  const [isVisible, setIsVisible] = useState(!isSecure);
  const showContent = !isSecure || isVisible;

  const displayValue = value != null ? `${value}%` : "--";
  const progressValue = Math.max(0, Math.min(value ?? 0, 100));
  const isPositive = (value ?? 0) >= 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.5,
        px: 1.5,
        py: 1.5,
        borderRadius: 4,
        background: `linear-gradient(135deg, ${theme.palette.customColors?.lightGreyColor || "#f8f9fa"} 0%, ${theme.palette.customColors?.white || "#fff"} 100%)`,
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        border: `1px solid ${theme.palette.customColors?.greyBorder || "#e9ecef"}`,
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "inline-flex",
          filter: showContent ? "none" : "blur(6px)",
          userSelect: showContent ? "auto" : "none",
          pointerEvents: showContent ? "auto" : "none",
        }}
      >
        <CircularProgress
          variant="determinate"
          value={100}
          size={56}
          thickness={4}
          sx={{ color: theme.palette.customColors?.greyBackground }}
        />
        <CircularProgress
          variant="determinate"
          value={progressValue}
          size={56}
          thickness={4}
          sx={{
            color: showContent
              ? isPositive
                ? theme.palette.customColors?.successGreen || "#2e7d32"
                : theme.palette.customColors?.errorRed || "#c62828"
              : theme.palette.customColors?.greyBackground,
            position: "absolute",
            left: 0,
            "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CustomTypography
            variant="body1"
            fontWeight={700}
            sx={{
              color: showContent
                ? isPositive
                  ? theme.palette.customColors?.successGreen
                  : theme.palette.customColors?.errorRed
                : theme.palette.customColors?.greyTextColor,
            }}
          >
            {displayValue}
          </CustomTypography>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          // filter: showContent ? "none" : "blur(6px)",
          // userSelect: showContent ? "auto" : "none",
        }}
      >
        <CustomTypography
          variant="body2"
          fontWeight={600}
          sx={{
            color: theme.palette.customColors?.greyTextColor,
          }}
        >
          {title}
        </CustomTypography>
        {isSecure && (
          <IconButton
            onClick={() => setIsVisible((prev) => !prev)}
            size="small"
            sx={{
              m: 0,
              p: 0,
              color: theme.palette.grey[500],
              "&:hover": {
                color: theme.palette.customColors?.blackBorder,
              },
              "& .MuiSvgIcon-root": { fontSize: 16 },
            }}
            aria-label={isVisible ? "Hide value" : "Show value"}
          >
            {isVisible ? (
              <VisibilityOffOutlinedIcon />
            ) : (
              <VisibilityOutlinedIcon />
            )}
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default PercentageStatCard;
